import React, { useState, useEffect, useRef, useCallback } from "react";

/* ---------------------------------------------------------------
   GEO DATA — Continent polygons (lon,lat) rasterized into a
   dot-matrix, plus a set of exchange "hub" cities used for trades.
--------------------------------------------------------------- */

const CONTINENTS = [
  // North America
  [[-165,68],[-165,60],[-140,60],[-130,55],[-125,48],[-124,40],[-117,32],[-105,20],
   [-97,18],[-90,16],[-85,10],[-80,8],[-77,8],[-80,25],[-82,30],[-81,31],[-75,35],
   [-70,41],[-67,45],[-60,47],[-55,51],[-65,58],[-75,62],[-85,68],[-95,70],[-110,72],
   [-130,70],[-140,70],[-155,71],[-165,68]],
  // South America
  [[-79,9],[-77,3],[-75,-2],[-70,-15],[-70,-20],[-68,-25],[-70,-33],[-73,-40],
   [-71,-48],[-68,-53],[-65,-55],[-60,-45],[-58,-45],[-55,-35],[-48,-25],[-40,-15],
   [-35,-8],[-38,-3],[-45,2],[-50,5],[-55,8],[-60,10],[-65,10],[-70,11],[-75,10],[-79,9]],
  // Africa
  [[-17,15],[-16,12],[-13,8],[-10,5],[-5,5],[3,6],[9,4],[9,-2],[12,-5],[13,-10],
   [12,-15],[14,-22],[16,-28],[18,-34],[22,-34],[26,-33],[30,-30],[32,-26],[35,-22],
   [40,-15],[43,-11],[41,-1],[43,5],[48,10],[45,12],[43,12],[39,15],[37,18],[35,20],
   [33,24],[32,28],[28,31],[25,32],[20,32],[15,32],[11,33],[9,32],[3,36],[-6,35],
   [-10,30],[-15,25],[-17,20],[-17,15]],
  // Europe
  [[-9,43],[-9,37],[-5,36],[0,38],[3,42],[7,44],[10,44],[13,45],[13,40],[16,40],
   [18,40],[20,39],[23,37],[26,40],[28,41],[29,45],[28,46],[30,46],[28,49],[24,50],
   [23,52],[20,54],[19,54],[14,54],[10,55],[8,56],[5,53],[3,51],[2,51],[-1,50],
   [-5,49],[-5,48],[-2,47],[-1,46],[-2,43],[-9,43]],
  // Asia
  [[28,49],[35,40],[40,38],[45,38],[50,40],[55,42],[60,45],[65,48],[70,50],[80,50],
   [90,50],[100,50],[110,48],[120,45],[130,45],[140,48],[150,55],[160,60],[170,65],
   [180,66],[180,60],[170,58],[160,55],[150,50],[140,45],[130,40],[122,32],[120,25],
   [112,20],[105,10],[100,5],[95,15],[90,22],[85,20],[78,15],[75,10],[70,20],[65,25],
   [60,25],[55,25],[50,27],[45,25],[40,35],[35,33],[33,31],[30,40],[28,49]],
  // Australia
  [[113,-22],[114,-26],[115,-31],[118,-33],[122,-34],[126,-32],[130,-31],[133,-32],
   [136,-35],[140,-38],[145,-38],[148,-37],[150,-35],[153,-28],[153,-25],[150,-22],
   [145,-17],[142,-11],[137,-12],[133,-12],[130,-12],[126,-14],[122,-17],[118,-20],[113,-22]],
];

const HUBS = [
  { id: "nyc", name: "New York",     code: "NYC", lon: -74.0,   lat: 40.7  },
  { id: "lax", name: "Los Angeles",  code: "LAX", lon: -118.24, lat: 34.05 },
  { id: "tor", name: "Toronto",      code: "YYZ", lon: -79.4,   lat: 43.65 },
  { id: "sao", name: "São Paulo",    code: "SAO", lon: -46.6,   lat: -23.5 },
  { id: "lon", name: "London",       code: "LON", lon: -0.1,    lat: 51.5  },
  { id: "ber", name: "Berlin",       code: "BER", lon: 13.4,    lat: 52.5  },
  { id: "zur", name: "Zürich",       code: "ZRH", lon: 8.5,     lat: 47.4  },
  { id: "mos", name: "Moscow",       code: "MOW", lon: 37.6,    lat: 55.75 },
  { id: "dub", name: "Dubai",        code: "DXB", lon: 55.3,    lat: 25.2  },
  { id: "mum", name: "Mumbai",       code: "BOM", lon: 72.8,    lat: 19.1  },
  { id: "sin", name: "Singapore",    code: "SIN", lon: 103.8,   lat: 1.35  },
  { id: "hkg", name: "Hong Kong",    code: "HKG", lon: 114.2,   lat: 22.3  },
  { id: "tyo", name: "Tokyo",        code: "TYO", lon: 139.7,   lat: 35.7  },
  { id: "seo", name: "Seoul",        code: "SEL", lon: 127.0,   lat: 37.6  },
  { id: "syd", name: "Sydney",       code: "SYD", lon: 151.2,   lat: -33.9 },
  { id: "jnb", name: "Johannesburg", code: "JNB", lon: 28.0,    lat: -26.2 },
];

const MAP_W = 1000;
const MAP_H = 500;
const LAT_MIN = -60;
const LAT_MAX = 75;

function project(lon, lat) {
  const x = ((lon + 180) / 360) * MAP_W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * MAP_H;
  return { x, y };
}

function pointInPolygon(lon, lat, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function buildLandDots() {
  const dots = [];
  for (let lat = LAT_MIN; lat <= LAT_MAX; lat += 2.4) {
    for (let lon = -180; lon <= 180; lon += 2.4) {
      for (let i = 0; i < CONTINENTS.length; i++) {
        if (pointInPolygon(lon, lat, CONTINENTS[i])) {
          dots.push(project(lon, lat));
          break;
        }
      }
    }
  }
  return dots;
}

const LAND_DOTS = buildLandDots();

function arcPath(p1, p2) {
  const mx = (p1.x + p2.x) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const curveHeight = Math.min(dist * 0.28, 85);
  const cy = (p1.y + p2.y) / 2 - curveHeight;
  return `M ${p1.x} ${p1.y} Q ${mx} ${cy} ${p2.x} ${p2.y}`;
}

function fmtUsd(n, decimals = 2) {
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/* ---------------------------------------------------------------
   COMPONENT
--------------------------------------------------------------- */

export default function ArbitrageEngine() {
  // Authentication & System State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isTrading, setIsTrading] = useState(false);

  // Data States
  const [btcPrice, setBtcPrice] = useState(0);
  const [btcBalance, setBtcBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [trades, setTrades] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);
  const [tradeCount, setTradeCount] = useState(0);
  const [activeArc, setActiveArc] = useState(null);
  const [nextTradeAt, setNextTradeAt] = useState(null);

  const [, forceTick] = useState(0);
  const timeoutRef = useRef(null);
  const idRef = useRef(0);

  // Check auth and load initial user data
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }

    setIsLoggedIn(true);

    async function loadData() {
      try {
        // Fetch BTC balance
        const balanceRes = await fetch("https://swiftexprofit.com/user/balance", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!balanceRes.ok) throw new Error("Failed to fetch balance");
        const balanceData = await balanceRes.json();
        setBtcBalance(balanceData.balance || 0);

        // Fetch BTC price
        const priceRes = await fetch("https://swiftexprofit.com/price/btc");
        if (!priceRes.ok) throw new Error("Failed to fetch BTC price");
        const priceData = await priceRes.json();
        setBtcPrice(priceData.btc_usd || 0);

        // Fetch user transactions
        const txRes = await fetch("https://swiftexprofit.com/user/transactions", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!txRes.ok) throw new Error("Failed to fetch transactions");
        const txData = await txRes.json();
        setTransactions(txData.transactions || []);

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong");
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // BTC price gentle random walk
  useEffect(() => {
    if (!btcPrice && isLoggedIn) return;
    const iv = setInterval(() => {
      setBtcPrice((p) => {
        const base = p || 66420;
        const next = base + (Math.random() - 0.5) * 30;
        return Math.max(1000, next);
      });
    }, 3500);
    return () => clearInterval(iv);
  }, [btcPrice, isLoggedIn]);

  // 1s ticker to redraw countdown
  useEffect(() => {
    const iv = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const executeTrade = useCallback(() => {
    const a = HUBS[Math.floor(Math.random() * HUBS.length)];
    let b = HUBS[Math.floor(Math.random() * HUBS.length)];
    while (b.id === a.id) b = HUBS[Math.floor(Math.random() * HUBS.length)];

    setBtcPrice((basePrice) => {
      const base = basePrice || 66420;
      const spreadPct = 0.04 + Math.random() * 0.22;
      const buyPrice = base * (1 - spreadPct / 200);
      const sellPrice = base * (1 + spreadPct / 200);
      const amount = +(0.02 + Math.random() * 0.28).toFixed(3);
      const profitUSD = (sellPrice - buyPrice) * amount;

      const profitBTC = profitUSD / base;
      setBtcBalance((prevBalance) => prevBalance + profitBTC);

      const buyCity = Math.random() > 0.5 ? a : b;
      const sellCity = buyCity.id === a.id ? b : a;

      const id = ++idRef.current;
      const p1 = project(buyCity.lon, buyCity.lat);
      const p2 = project(sellCity.lon, sellCity.lat);

      setActiveArc({ id, from: buyCity, to: sellCity, d: arcPath(p1, p2), fading: false });

      setTimeout(() => {
        setActiveArc((cur) => (cur && cur.id === id ? { ...cur, fading: true } : cur));
      }, 1900);
      setTimeout(() => {
        setActiveArc((cur) => (cur && cur.id === id ? null : cur));
      }, 2500);

      setTrades((cur) => [
        {
          id,
          time: new Date().toLocaleTimeString("en-US", { hour12: false }),
          buyCity,
          sellCity,
          buyPrice,
          sellPrice,
          amount,
          profit: profitUSD,
          spreadPct,
        },
        ...cur,
      ].slice(0, 9));

      setTotalProfit((p) => p + profitUSD);
      setTradeCount((c) => c + 1);

      return base;
    });
  }, []);

  // Trading Scheduler Controlled by isTrading
  useEffect(() => {
    if (!isTrading) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setNextTradeAt(null);
      return;
    }

    function schedule() {
      const delay = 30000 + Math.random() * 15000; // 30–45s
      setNextTradeAt(Date.now() + delay);
      timeoutRef.current = setTimeout(() => {
        executeTrade();
        schedule();
      }, delay);
    }

    const firstDelay = 3000 + Math.random() * 2000;
    setNextTradeAt(Date.now() + firstDelay);
    timeoutRef.current = setTimeout(() => {
      executeTrade();
      schedule();
    }, firstDelay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isTrading, executeTrade]);

  const toggleTrading = () => {
    setIsTrading((prev) => !prev);
  };

  const remaining = nextTradeAt ? Math.max(0, Math.round((nextTradeAt - Date.now()) / 1000)) : 0;

  if (loading) return <div style={{ color: "#e7ecf3", padding: "28px", fontFamily: "sans-serif" }}>Loading engine metrics...</div>;
  if (error) return <div style={{ color: "#ff4d4d", padding: "28px", fontFamily: "sans-serif" }}>Error: {error}</div>;

  /* ---------------------------------------------------------------
     UNAUTHENTICATED VIEW
  --------------------------------------------------------------- */
  if (!isLoggedIn) {
    return (
      <div className="arb-root">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

          .arb-root {
            --bg: #06090e;
            --panel: rgba(13, 20, 32, 0.7);
            --panel-border: rgba(255, 255, 255, 0.08);
            --text: #f0f4f8;
            --text-dim: #8a96a8;
            --teal: #00e6a8;
            background: radial-gradient(140% 120% at 50% -10%, #0c1626 0%, var(--bg) 80%);
            color: var(--text);
            font-family: 'JetBrains Mono', monospace;
            padding: 48px 32px;
            border-radius: 16px;
            border: 1px solid var(--panel-border);
            box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
            max-width: 900px;
            margin: 0 auto;
          }
          .welcome-hero {
            text-align: center;
            margin-bottom: 40px;
          }
          .welcome-hero h1 {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 36px;
            font-weight: 700;
            color: #ffffff;
            margin: 12px 0;
          }
          .welcome-hero p {
            color: var(--text-dim);
            font-size: 15px;
            line-height: 1.6;
            max-width: 650px;
            margin: 0 auto;
          }
          .badge {
            display: inline-block;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.15em;
            color: var(--teal);
            background: rgba(0, 230, 168, 0.1);
            border: 1px solid rgba(0, 230, 168, 0.3);
            padding: 6px 14px;
            border-radius: 20px;
            text-transform: uppercase;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
          }
          .info-card {
            background: var(--panel);
            border: 1px solid var(--panel-border);
            padding: 24px;
            border-radius: 12px;
            backdrop-filter: blur(8px);
          }
          .info-card h3 {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 18px;
            color: #fff;
            margin-top: 0;
            margin-bottom: 10px;
          }
          .info-card p {
            font-size: 13px;
            color: var(--text-dim);
            line-height: 1.5;
            margin: 0;
          }
          .cta-wrapper {
            text-align: center;
          }
          .btn-login {
            background: var(--teal);
            color: #06090e;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 16px;
            padding: 14px 36px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0 0 20px rgba(0, 230, 168, 0.3);
          }
          .btn-login:hover {
            transform: translateY(-2px);
            box-shadow: 0 0 30px rgba(0, 230, 168, 0.5);
          }
        `}</style>

        <div className="welcome-hero">
          <span className="badge">Autonomous Engine</span>
          <h1>Crypto Arbitrage Trading</h1>
          <p>
            Capitalize on real-time price variances of Bitcoin across major global exchanges with automated cross-hub execution.
          </p>
        </div>

        <div className="info-grid">
          <div className="info-card">
            <h3>What is Arbitrage?</h3>
            <p>
              Crypto arbitrage is a trading strategy that profits from price differences of the same asset across different exchanges. Buying low on one exchange and selling high on another generates risk-mitigated profit.
            </p>
          </div>
          <div className="info-card">
            <h3>Automated Execution</h3>
            <p>
              Our ultra-low latency algorithm scans liquidity hubs in New York, London, Tokyo, and beyond, completing spatial arbitrage cycles within milliseconds.
            </p>
          </div>
          <div className="info-card">
            <h3>Zero Manual Overhead</h3>
            <p>
              Connect your account, launch the engine, and let our system automatically scan order books, evaluate spreads, and accumulate net yields directly into your wallet.
            </p>
          </div>
        </div>

        <div className="cta-wrapper">
          <button 
            className="btn-login" 
            onClick={() => window.location.href = "/login"}
          >
            Log In First to Start Trading
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------
     AUTHENTICATED DASHBOARD VIEW
  --------------------------------------------------------------- */
  return (
    <div className="arb-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

        .arb-root {
          --bg: #06090e;
          --panel: rgba(13, 20, 32, 0.7);
          --panel-border: rgba(255, 255, 255, 0.08);
          --panel-hover: rgba(255, 255, 255, 0.03);
          --text: #f0f4f8;
          --text-dim: #8a96a8;
          --text-dimmer: #525e70;
          --btc: #ff9f1c;
          --teal: #00e6a8;
          --dot-land: rgba(255, 255, 255, 0.12);
          --dot-hub: #3a475a;

          background: radial-gradient(140% 120% at 50% -10%, #0c1626 0%, var(--bg) 80%);
          color: var(--text);
          font-family: 'JetBrains Mono', monospace;
          padding: 32px;
          border-radius: 16px;
          box-sizing: border-box;
          width: 100%;
          border: 1px solid var(--panel-border);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
        }
        .arb-root * { box-sizing: border-box; }

        .arb-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--panel-border);
          margin-bottom: 28px;
        }

        .eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.2em;
          color: var(--teal);
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          text-transform: uppercase;
        }
        .eyebrow .dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--teal);
          box-shadow: 0 0 10px var(--teal);
        }
        .eyebrow .dot.active {
          animation: blink 1.8s ease-in-out infinite;
        }
        .eyebrow .dot.idle {
          background: #525e70;
          box-shadow: none;
        }
        @keyframes blink { 0%,100%{opacity:1; transform: scale(1);} 50%{opacity:0.3; transform: scale(0.85);} }

        .arb-title-block h1 {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 700;
          font-size: 28px;
          letter-spacing: -0.02em;
          margin: 0;
          color: #ffffff;
        }

        .arb-stats-wrapper {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .btn-trade-control {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 700;
          font-size: 14px;
          padding: 12px 24px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .btn-trade-control.start {
          background: var(--teal);
          color: #06090e;
          box-shadow: 0 0 15px rgba(0, 230, 168, 0.3);
        }
        .btn-trade-control.start:hover {
          box-shadow: 0 0 25px rgba(0, 230, 168, 0.5);
          transform: translateY(-1px);
        }
        .btn-trade-control.stop {
          background: rgba(255, 77, 77, 0.15);
          color: #ff4d4d;
          border: 1px solid rgba(255, 77, 77, 0.3);
        }
        .btn-trade-control.stop:hover {
          background: rgba(255, 77, 77, 0.25);
        }

        .arb-stats { 
          display: flex; 
          gap: 16px; 
          flex-wrap: wrap; 
        }
        .stat { 
          background: var(--panel);
          border: 1px solid var(--panel-border);
          padding: 12px 18px;
          border-radius: 10px;
          min-width: 140px;
          backdrop-filter: blur(8px);
        }
        .stat .label {
          font-size: 10px; 
          color: var(--text-dim); 
          letter-spacing: 0.08em;
          margin-bottom: 4px; 
          text-transform: uppercase;
        }
        .stat .value {
          font-size: 16px; 
          font-weight: 600;
          font-variant-numeric: tabular-nums;
        }
        .stat .value.teal { color: var(--teal); }
        .stat .value.btc { color: var(--btc); }

        .arb-body {
          display: grid;
          grid-template-columns: 1.8fr 1fr;
          gap: 24px;
        }
        @media (max-width: 960px) {
          .arb-body { grid-template-columns: 1fr; }
        }

        .arb-map-panel {
          background: var(--panel);
          border: 1px solid var(--panel-border);
          border-radius: 12px;
          padding: 16px;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(12px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .map-caption {
          font-size: 11px;
          letter-spacing: 0.1em;
          color: var(--text-dimmer);
          padding-top: 12px;
          display: flex;
          justify-content: space-between;
          border-top: 1px solid rgba(255, 255, 255, 0.04);
          margin-top: 8px;
        }
        .map-caption .countdown { color: var(--text-dim); }
        .map-caption .countdown b { color: var(--teal); font-weight: 600; }

        .sweep {
          position: absolute;
          top: 0; bottom: 0; width: 30%;
          background: linear-gradient(90deg, transparent, rgba(0, 230, 168, 0.03), transparent);
          animation: sweep 8s linear infinite;
          pointer-events: none;
        }
        @keyframes sweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }

        .hub-dot { fill: var(--dot-hub); transition: fill 0.3s; }
        .hub-ring {
          fill: none; stroke: var(--dot-hub); stroke-width: 1;
          opacity: 0.4;
        }
        .hub-dot.buying { fill: var(--btc); }
        .hub-dot.selling { fill: var(--teal); }

        .pulse-ring {
          fill: none;
          stroke-width: 2;
          transform-origin: center;
          animation: pulseGrow 1.3s ease-out forwards;
        }
        .pulse-ring.buy { stroke: var(--btc); }
        .pulse-ring.sell { stroke: var(--teal); }
        @keyframes pulseGrow {
          0% { r: 4; opacity: 0.9; stroke-width: 2; }
          100% { r: 22; opacity: 0; stroke-width: 0.5; }
        }

        .arc-path {
          fill: none;
          stroke: url(#arcGradient);
          stroke-width: 2;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: drawArc 1.15s cubic-bezier(.2,.7,.3,1) forwards;
          filter: drop-shadow(0 0 6px rgba(255, 159, 28, 0.4));
        }
        .arc-path.fading { animation: fadeOut 0.6s ease-out forwards; }
        @keyframes drawArc {
          0% { stroke-dashoffset: 100; opacity: 0; }
          10% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes fadeOut { to { opacity: 0; } }

        .packet {
          fill: #ffffff;
          filter: drop-shadow(0 0 6px var(--btc));
        }

        .feed-panel {
          background: var(--panel);
          border: 1px solid var(--panel-border);
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          min-height: 380px;
          max-height: 520px;
          backdrop-filter: blur(12px);
          overflow: hidden;
        }
        .feed-header {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          color: var(--text-dim);
          padding: 16px 20px;
          border-bottom: 1px solid var(--panel-border);
          background: rgba(255, 255, 255, 0.01);
        }
        .feed-list {
          overflow-y: auto;
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .feed-empty {
          padding: 32px 20px;
          color: var(--text-dimmer);
          font-size: 12px;
          text-align: center;
        }
        .feed-row {
          padding: 12px 14px;
          border-radius: 8px;
          border: 1px solid transparent;
          background: rgba(255, 255, 255, 0.015);
          animation: rowIn 0.35s ease-out;
          transition: background 0.2s, border-color 0.2s;
        }
        .feed-row:hover {
          background: var(--panel-hover);
          border-color: rgba(255, 255, 255, 0.05);
        }
        @keyframes rowIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .feed-row-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .feed-route {
          font-size: 13px;
          font-weight: 600;
          color: var(--text);
        }
        .feed-route .arrow { color: var(--text-dimmer); margin: 0 6px; }
        .feed-time { font-size: 11px; color: var(--text-dimmer); }
        .feed-row-bottom {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: var(--text-dim);
        }
        .feed-profit { color: var(--teal); font-weight: 600; }
      `}</style>

      <div className="arb-header">
        <div className="arb-title-block">
          <div className="eyebrow">
            <span className={`dot ${isTrading ? "active" : "idle"}`} />
            {isTrading ? "AI ENGINE ACTIVE" : "ENGINE STANDBY"}
          </div>
          <h1>Arbitrage Engine</h1>
        </div>

        <div className="arb-stats-wrapper">
          <button 
            className={`btn-trade-control ${isTrading ? "stop" : "start"}`}
            onClick={toggleTrading}
          >
            {isTrading ? "Pause Engine" : "Start Trading"}
          </button>

          <div className="arb-stats">
            <div className="stat">
              <div className="label">User Balance</div>
              <div className="value teal">${fmtUsd(btcBalance * btcPrice)}</div>
              <div style={{ fontSize: "10px", color: "var(--text-dim)", marginTop: "2px" }}>
                {btcBalance.toFixed(6)} BTC
              </div>
            </div>
            <div className="stat">
              <div className="label">Total P&amp;L</div>
              <div className="value teal">+${fmtUsd(totalProfit)}</div>
            </div>
            <div className="stat">
              <div className="label">Trades Executed</div>
              <div className="value">{tradeCount}</div>
            </div>
            <div className="stat">
              <div className="label">BTC / USD</div>
              <div className="value btc">${fmtUsd(btcPrice)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="arb-body">
        <div className="arb-map-panel">
          <div className="sweep" />
          <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} width="100%" style={{ display: "block" }}>
            <defs>
              <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ff9f1c" />
                <stop offset="100%" stopColor="#00e6a8" />
              </linearGradient>
            </defs>

            {/* Subtle Grid Lines */}
            {Array.from({ length: 9 }).map((_, i) => (
              <line key={`v${i}`} x1={(i * MAP_W) / 8} y1={0} x2={(i * MAP_W) / 8} y2={MAP_H} stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
            ))}
            {Array.from({ length: 5 }).map((_, i) => (
              <line key={`h${i}`} x1={0} y1={(i * MAP_H) / 4} x2={MAP_W} y2={(i * MAP_H) / 4} stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
            ))}

            {/* Dot-matrix Landmass */}
            {LAND_DOTS.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="1.1" fill="var(--dot-land)" />
            ))}

            {/* Active Arc and Packet */}
            {activeArc && (
              <g>
                <path d={activeArc.d} className={`arc-path${activeArc.fading ? " fading" : ""}`} pathLength="100" />
                <circle r="3.5" className="packet">
                  <animateMotion dur="1.15s" fill="freeze" path={activeArc.d} keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.06;0.85;1" dur="1.3s" fill="freeze" />
                </circle>
              </g>
            )}

            {/* Hub Nodes */}
            {HUBS.map((h) => {
              const p = project(h.lon, h.lat);
              const isBuy = activeArc && activeArc.from.id === h.id;
              const isSell = activeArc && activeArc.to.id === h.id;
              return (
                <g key={h.id}>
                  <circle cx={p.x} cy={p.y} r="5" className="hub-ring" />
                  <circle cx={p.x} cy={p.y} r="2.2" className={`hub-dot${isBuy ? " buying" : ""}${isSell ? " selling" : ""}`} />
                  {isBuy && <circle cx={p.x} cy={p.y} className="pulse-ring buy" />}
                  {isSell && (
                    <circle cx={p.x} cy={p.y} className="pulse-ring sell" style={{ animationDelay: "1.1s" }} />
                  )}
                </g>
              );
            })}
          </svg>
          <div className="map-caption">
            <span>EQUIRECTANGULAR · GLOBAL EXCHANGE NODES</span>
            <span className="countdown">
              {isTrading ? (
                <>NEXT SCAN IN <b>{remaining}s</b></>
              ) : (
                <>ENGINE PAUSED</>
              )}
            </span>
          </div>
        </div>

        <div className="feed-panel">
          <div className="feed-header">LIVE TRADE FEED</div>
          <div className="feed-list">
            {trades.length === 0 && (
              <div className="feed-empty">
                {isTrading 
                  ? "Scanning global exchanges for arbitrage opportunities…" 
                  : "Engine paused. Press 'Start Trading' to begin execution."}
              </div>
            )}
            {trades.map((t) => (
              <div className="feed-row" key={t.id}>
                <div className="feed-row-top">
                  <div className="feed-route">
                    {t.buyCity.code}<span className="arrow">→</span>{t.sellCity.code}
                  </div>
                  <div className="feed-time">{t.time}</div>
                </div>
                <div className="feed-row-bottom">
                  <span>{t.amount} BTC · {t.spreadPct.toFixed(2)}% spread</span>
                  <span className="feed-profit">+${fmtUsd(t.profit)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}